import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';

type Idea = { id: string; content: string; version: number };
const api = '/api/serq/lac-epaule-2026/ideas/';
async function request(url: string, init?: RequestInit) {
  const response = await fetch(url, { credentials: 'include', ...init });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || 'Impossible d’accéder aux idées. Réessayez.');
  }
  return response.json();
}

type LacEpauleIdeasModalProps = {
  owner: string;
  name: string;
  avatar: string;
  onClose: () => void;
};

export default function LacEpauleIdeasModal({ owner, name, avatar, onClose }: LacEpauleIdeasModalProps) {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [content, setContent] = useState('');
  const [editing, setEditing] = useState<Idea | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const closeButton = useRef<HTMLButtonElement>(null);
  const refreshedIdea = editing ? ideas.find(idea => idea.id === editing.id) : undefined;
  const needsReconciliation = !!editing && (!refreshedIdea || refreshedIdea.version !== editing.version);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    request(api + owner).then(data => { if (active) setIdeas(data); })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [owner, reload]);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (saving || loading || needsReconciliation || !content.trim()) return;
    setSaving(true); setError('');
    try {
      const idea: Idea = await request(api + owner + (editing ? `/${editing.id}` : ''), {
        method: editing ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, ...(editing ? { version: editing.version } : {}) }),
      });
      setIdeas(previous => editing ? previous.map(item => item.id === idea.id ? idea : item) : [...previous, idea]);
      setContent(''); setEditing(null);
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur de sauvegarde'); }
    finally { setSaving(false); requestAnimationFrame(() => closeButton.current?.focus()); }
  }
  return (
    <Dialog open onKeyDownCapture={event => { if (event.key === 'Escape' && !saving) { event.preventDefault(); event.stopPropagation(); onClose(); } }} onClose={() => { if (!saving) onClose(); }} className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-slate-950/60" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto p-4 sm:p-8">
        <div className="flex min-h-full items-center justify-center">
          <Dialog.Panel className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={avatar}
                  alt={`Avatar de ${name}`}
                  className="h-20 w-20 shrink-0 rounded-full border-4 border-white object-cover shadow-lg ring-2 ring-blue-200"
                />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Intervenant</p>
                  <Dialog.Title className="mt-1 text-2xl font-bold text-slate-900">Mes idées</Dialog.Title>
                  <Dialog.Description className="mt-1 text-sm text-slate-600">
                    {name} · Lac à l’épaule décembre 2026
                  </Dialog.Description>
                </div>
              </div>
              <button ref={closeButton} type="button" aria-label="Fermer" aria-disabled={saving} onClick={() => { if (!saving) onClose(); }} className="rounded-lg p-2 hover:bg-slate-100 disabled:opacity-50"><X /></button>
            </div>
            {error && <div role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-red-800">{error}</div>}
            <button type="button" disabled={loading || saving} onClick={() => { setReload(value => value + 1); }} className="mt-4 text-sm font-semibold text-blue-700 disabled:opacity-50">Recharger les idées</button>
            <div aria-live="polite" className="my-5 max-h-72 overflow-y-auto">
              {loading ? <p>Chargement des idées…</p> : ideas.length === 0 ? <p className="text-slate-500">Aucune idée pour le moment.</p> :
                <ul className="space-y-3">{ideas.map(idea => <li key={idea.id} className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                  <p className="whitespace-pre-wrap break-words text-slate-800">{idea.content}</p>
                  <button type="button" disabled={saving} onClick={() => { setEditing(idea); setContent(idea.content); }} className="mt-2 text-sm font-semibold text-blue-700">Modifier l’idée</button>
                </li>)}</ul>}
            </div>
            <form onSubmit={save} className="border-t border-slate-200 pt-5">
              {!loading && needsReconciliation && <div role="status" className="mb-4 rounded-xl bg-amber-50 p-3 text-amber-900">
                <p>{refreshedIdea ? 'Cette idée a changé. Votre brouillon est conservé. Comparez-le à la version rechargée avant de confirmer.' : 'Cette idée n’est plus disponible. Votre brouillon reste associé à l’idée originale et ne peut pas être enregistré.'}</p>
                {refreshedIdea && <button type="button" disabled={saving} onClick={() => setEditing(refreshedIdea)} className="mt-2 text-sm font-semibold underline">Conserver mon brouillon sur cette version</button>}
              </div>}
              <label htmlFor="lac-idea" className="font-semibold text-slate-800">Votre idée</label>
              <textarea id="lac-idea" required maxLength={5000} rows={4} disabled={saving || loading} value={content} onChange={event => setContent(event.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 p-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200" />
              <p className="text-xs text-slate-500">5 000 caractères maximum. Enregistré pour tous les utilisateurs autorisés.</p>
              <div className="mt-4 flex gap-3">
                <button type="submit" disabled={saving || loading || needsReconciliation || !content.trim()} className="rounded-xl bg-blue-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter l’idée'}</button>
                {editing && <button type="button" disabled={saving} onClick={() => { setEditing(null); setContent(''); }} className="rounded-xl border px-4 py-2">Annuler la modification</button>}
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
